import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button.tsx';
import { Markdown } from '../Markdown/Markdown.tsx';
import { type AnswerWidgets, collectAnswerWidgets } from './answerWidgets.ts';
import { ChatAiBubble } from './components/ChatAiBubble.tsx';
import { ChatInput } from './components/ChatInput.tsx';
import { ChatMessage } from './components/ChatMessage.tsx';
import { ChatThinkingContent } from './components/ChatThinkingContent.tsx';
import { SuggestionCard } from './components/SuggestionCard.tsx';
import { getMessageId } from './messageId.ts';
import { type Message, MessageRole, type SuggestionOption } from './types.ts';

const SCROLL_BOTTOM_TOLERANCE_PX = 8;

function scrollContainerToBottom(container: HTMLElement, behavior: ScrollBehavior) {
  container.scrollTo({ top: container.scrollHeight, behavior });
}

function isContainerNearBottom(container: HTMLElement) {
  return container.scrollHeight - container.scrollTop - container.clientHeight <= SCROLL_BOTTOM_TOLERANCE_PX;
}

export type AiChatProps = {
  /**
   * Controls display of the attachments section.
   * If not provided, the attachments section will not be displayed.
   */
  attachments?: {
    files: File[];
    onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRemove: (file: File) => void;
  };
  cancelMessage?: () => void;
  /**
   * When set, displays an error message below the conversation (e.g. "Something went wrong...").
   */
  errorMessage?: string | null;
  headerText: string;
  subHeaderText?: string;
  isThinking: boolean;
  thinkingPlaceholderMessages?: readonly string[];
  isLoading: boolean;
  messages: Message[];
  /**
   * Controls display of the message actions section for assistant messages.
   * If not provided, the message actions section will not be displayed.
   * `widgets` are the answer widgets of the message's invocation, for actions that copy the answer.
   */
  messageActions?: (message: Message, widgets?: AnswerWidgets) => React.ReactNode;
  sendMessage: (query: string) => void;
  suggestions?: SuggestionOption[];
  thinkingProcessMessages?: string[];
};

export function AiChat({
  attachments,
  cancelMessage,
  errorMessage,
  headerText,
  subHeaderText,
  isThinking,
  thinkingPlaceholderMessages,
  isLoading,
  messages,
  messageActions,
  sendMessage,
  suggestions,
  thinkingProcessMessages = [],
}: AiChatProps) {
  const { t } = useTranslation();
  const inputRef = useRef<{ setValue: (value: string) => void }>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldFollowRef = useRef(true);
  const previousMessagesLengthRef = useRef(0);
  const lastForcedAgentTimestampRef = useRef<string | null>(null);

  const hasMessages = messages.length > 0 || errorMessage;
  const widgetsByInvocation = useMemo(() => collectAnswerWidgets(messages), [messages]);
  const showSuggestionsAsCards =
    suggestions != null &&
    suggestions.length > 0 &&
    !hasMessages &&
    !isThinking &&
    !errorMessage &&
    !isLoading &&
    suggestions.some(s => s.description != null);

  const handleSuggestionClick = useCallback((message: string) => {
    if (inputRef.current) {
      inputRef.current.setValue(message);
    }
  }, []);

  const handleContainerScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      shouldFollowRef.current = isContainerNearBottom(container);
    }
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    const previousLength = previousMessagesLengthRef.current;
    const appendedMessages = previousLength < messages.length ? messages.slice(previousLength) : [];
    let newFinalAgentTimestamp: string | null = null;
    let appendedUserMessage = false;
    let appendedEvent = false;

    for (const message of appendedMessages) {
      if (message.role === MessageRole.EVENT || message.role === MessageRole.WIDGET) {
        appendedEvent = true;
      } else if (message.role === MessageRole.USER) {
        appendedUserMessage = true;
      } else if (message.timestamp !== lastForcedAgentTimestampRef.current) {
        newFinalAgentTimestamp = message.timestamp;
      }
    }

    if (newFinalAgentTimestamp !== null) {
      shouldFollowRef.current = true;
      scrollContainerToBottom(container, 'smooth');
      lastForcedAgentTimestampRef.current = newFinalAgentTimestamp;
    } else if (appendedUserMessage) {
      shouldFollowRef.current = true;
      scrollContainerToBottom(container, 'smooth');
    } else if (appendedEvent && shouldFollowRef.current) {
      scrollContainerToBottom(container, 'auto');
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages]);

  return (
    <Stack
      direction="column"
      sx={[
        {
          gap: 1,
          alignItems: 'center',
          justifyContent: 'center',
        },
        theme => ({
          minWidth: 0,
          width: '100%',
          height: '100%',
          padding: 2,
          backgroundColor: theme.palette.background.boxLight,
        }),
      ]}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
            borderRadius: 1,
            color: theme.palette.primary.main,
          })}
        >
          <AutoAwesomeOutlinedIcon sx={{ color: 'inherit' }} fontSize="small" />
        </Box>
        <Typography variant="h1">{headerText}</Typography>
      </Stack>
      <Typography variant="body2" component="p" sx={{ fontSize: 15 }}>
        {subHeaderText ?? t('Pick a Quick Chip or Type Your Question Below.')}
      </Typography>

      <Box
        ref={messagesContainerRef}
        onScroll={handleContainerScroll}
        data-test="messages-container"
        sx={{
          flex: hasMessages ? 1 : undefined,
          width: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transition: 'max-height 0.25s ease-out, flex 0.25s ease-out',
          gap: '12px',
          padding: '0 5%',
        }}
      >
        {isLoading && (
          <>
            <Skeleton
              animation="wave"
              sx={{ width: '75%', height: 120, alignSelf: 'flex-end', flexShrink: 0, transform: 'unset' }}
            />
            <Skeleton
              animation="wave"
              sx={{ width: '75%', height: 360, alignSelf: 'flex-start', flexShrink: 0, transform: 'unset' }}
            />
          </>
        )}
        {messages.map(message => {
          if (message.role === MessageRole.EVENT || message.role === MessageRole.WIDGET) {
            return null;
          }
          const messageId = getMessageId(message);
          const widgets =
            message.role === MessageRole.AGENT ? widgetsByInvocation.get(message.invocation_id) : undefined;
          return (
            <ChatMessage
              key={messageId}
              messageId={messageId}
              isUserMessage={message.role === MessageRole.USER}
              icon={message.role === MessageRole.AGENT && <ChatAiBubble />}
              fullWidth={widgets !== undefined}
            >
              {message.role === MessageRole.AGENT && message.summary && (
                <Stack
                  data-test="message-summary"
                  direction="column"
                  sx={{
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Markdown content={message.summary} fontSize={15} widgets={widgets} />
                </Stack>
              )}
              <Markdown content={message.text} fontSize={15} widgets={widgets} />
              {message.role === MessageRole.AGENT && messageActions !== undefined && messageActions(message, widgets)}
            </ChatMessage>
          );
        })}
        {isThinking && (
          <ChatMessage messageId="thinking" isUserMessage={false} icon={<ChatAiBubble animate={true} />}>
            <ChatThinkingContent
              key={messages.at(-1)?.invocation_id ?? 'thinking'}
              id={messages.at(-1)?.invocation_id}
              thinkingPlaceholderMessages={thinkingPlaceholderMessages}
              thinkingProcessMessages={thinkingProcessMessages}
            />
          </ChatMessage>
        )}
        {errorMessage && (
          <ChatMessage messageId="error" isUserMessage={false} icon={<ChatAiBubble variant="error" />}>
            <Typography
              variant="body2"
              color="error"
              sx={{
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              {errorMessage}
            </Typography>
          </ChatMessage>
        )}
      </Box>

      <Stack
        direction="column"
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 1,
          width: 'fit-content',
          minWidth: 'min(100%, 1000px)',
          maxWidth: '100%',
        }}
      >
        {showSuggestionsAsCards && (
          <Stack
            direction="row"
            sx={{
              contain: 'inline-size',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              mb: 4,
              width: '100%',
            }}
          >
            {suggestions.map(suggestion => (
              <SuggestionCard
                key={suggestion.label}
                suggestion={suggestion}
                onClick={() => handleSuggestionClick(suggestion.message)}
              />
            ))}
          </Stack>
        )}
        <ChatInput
          attachments={attachments}
          cancelMessage={cancelMessage}
          sendButton={{ onClick: sendMessage, isLoading: isThinking }}
          minRows={hasMessages ? 1 : 3}
          inputRef={inputRef}
        />
        {suggestions !== undefined && suggestions.length > 0 && !showSuggestionsAsCards && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            {suggestions.map(({ label, message, Icon }) => (
              <Button
                key={label}
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={
                  <Box component="span" sx={{ color: 'primary.main', display: 'inline-flex' }}>
                    <Icon fontSize="small" />
                  </Box>
                }
                onClick={() => handleSuggestionClick(message)}
                sx={theme => ({
                  textTransform: 'none',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  color: theme.palette.text.primary,
                })}
              >
                {label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
